import { Schema as S } from "effect";
import { defineTaggedUnion } from "foldkit/schema";

import { i18next } from "../i18n";

/** Every way a request can fail, as data on the Message path (KTD5). */
export const ApiError = defineTaggedUnion({
	NotFound: { message: S.String },
	LimitExceeded: {
		metric: S.String,
		current: S.Number,
		limit: S.Number,
		period: S.String,
	},
	Http: { status: S.Number, message: S.String },
	Decode: { path: S.String },
	Transport: { message: S.String },
	Unauthenticated: {},
	/** The circle turned AI off in Settings; the server made no AI call. */
	AiDisabled: {},
});
export type ApiError = typeof ApiError.Type;

/** Which plan limit was hit and what to do about it; `metric` is the server's `MetricName`. */
const LIMIT_TEXT: ReadonlyMap<string, (limit: number) => string> = new Map([
	["records", (limit: number) => i18next.t("limits.records", { limit })],
	["entries", (limit: number) => i18next.t("limits.entries", { limit })],
	// A zero storage limit means the plan has no file uploads at all (free).
	[
		"storage_bytes",
		(limit: number) =>
			i18next.t(limit === 0 ? "limits.uploadsPlus" : "limits.storage"),
	],
	["ai_calls", (limit: number) => i18next.t("limits.aiCalls", { limit })],
]);

/**
 * v1 surfaced `error.message` from its thrown `Error`s; that text is kept, with the
 * variant and detail appended in parentheses so a page banner says *why* it failed.
 * A plan limit is the exception: it reads as a translated sentence a user can act on.
 */
export const toMessage = (error: ApiError): string =>
	ApiError.match(error, {
		NotFound: ({ message }) => `${message} (NotFound)`,
		LimitExceeded: ({ metric, current, limit }) =>
			// Before translations initialise `t` returns undefined, which a page Model's schema rejects.
			(LIMIT_TEXT.get(metric)?.(limit) ?? i18next.t("limits.other")) ||
			`Limit exceeded for ${metric}: ${current}/${limit}`,
		Http: ({ status, message }) => `${message} (Http ${status})`,
		Decode: ({ path }) => `Unexpected response (Decode: ${path || "<root>"})`,
		Transport: ({ message }) => `${message} (Transport)`,
		Unauthenticated: () => "Unauthenticated",
		// Before translations initialise `t` returns undefined, which a page Model's schema rejects.
		AiDisabled: () =>
			i18next.t("settings.ai.refused") || "AI is turned off for this circle.",
	});
